import React, { useState, useEffect } from "react";
import {
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Stack,
  Text,
  Spinner,
  Alert,
  AlertIcon,
  Flex,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  useColorModeValue,
  Button,
} from "@chakra-ui/react";
import { FaSync, FaPrint, FaSearch } from "react-icons/fa";
import { BASE_URL } from "../../Globalapi/Globalapi";

const ResultsTable = ({
  categoryResults,
  currentPage,
  rowsPerPage,
  handlePageChange,
  searchQuery,
}) => {
  const { students, subjects, tableData } = categoryResults;
  const borderColor = useColorModeValue("gray.200", "gray.700");

  // Filter data based on search query
  const filteredData = tableData.filter((row) =>
    row.student.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate pagination
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentPageData = filteredData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);

  return (
    <Box>
      <Box overflowX="auto">
        <Table
          size="lg"
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          boxShadow="md"
          p={4}
        >
          <Thead>
            <Tr>
              <Th
                width="200px"
                textAlign="center"
                fontSize="lg"
                fontWeight="bold"
                color="teal.700"
              >
                Student Name
              </Th>
              {subjects.map((subject) => (
                <Th
                  key={subject}
                  textAlign="center"
                  minWidth="200px"
                  fontSize="lg"
                  fontWeight="bold"
                  color="teal.700"
                >
                  <Box>
                    <Text textAlign="center">{subject}</Text>
                    <Box
                      display="grid"
                      gridTemplateColumns="repeat(5, 1fr)"
                      gap={2}
                      mt={2}
                    >
                      <Text textAlign="center" fontSize="sm" color="gray.600">
                        IE
                      </Text>
                      <Text textAlign="center" fontSize="sm" color="gray.600">
                        WE
                      </Text>
                      <Text textAlign="center" fontSize="sm" color="gray.600">
                        Total
                      </Text>
                      <Text textAlign="center" fontSize="sm" color="gray.600">
                        Grade
                      </Text>
                      <Text textAlign="center" fontSize="sm" color="gray.600">
                        Compliment
                      </Text>
                    </Box>
                  </Box>
                </Th>
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {currentPageData.map((row, index) => (
              <Tr key={index} _hover={{ bg: "gray.100" }}>
                <Td
                  fontWeight="medium"
                  textAlign="center"
                  width="200px"
                  fontSize="md"
                  color="gray.700"
                >
                  {row.student}
                </Td>
                {subjects.map((subject) => (
                  <Td key={subject} textAlign="center" minWidth="200px">
                    <Box
                      display="grid"
                      gridTemplateColumns="repeat(5, 1fr)"
                      gap={2}
                      mt={2}
                    >
                      <Text textAlign="center" fontSize="sm" color="gray.600">
                        {row[subject]?.internal_mark || "N/A"}
                      </Text>
                      <Text textAlign="center" fontSize="sm" color="gray.600">
                        {row[subject]?.written_mark || "N/A"}
                      </Text>
                      <Text textAlign="center" fontSize="sm" color="gray.600">
                        {row[subject]?.marks || "N/A"}
                      </Text>
                      <Text textAlign="center" fontSize="sm" color="gray.600">
                        {row[subject]?.grade || "N/A"}
                      </Text>
                      <Text textAlign="center" fontSize="sm" color="gray.600">
                        {row[subject]?.compliment || "N/A"}
                      </Text>
                    </Box>
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
      {currentPageData.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Text fontSize="lg" color="gray.500">
            No results found
          </Text>
        </Box>
      ) : (
        <Box p={1}>
          <Flex justify="end" align="center" className="sb2" p={3}>
            <Button
              onClick={() => handlePageChange(currentPage - 1)}
              isDisabled={currentPage === 1}
            >
              Prev
            </Button>
            <Stack direction="row" mx={2}>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(
                  (page) =>
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                )
                .map((page, index, array) => (
                  <React.Fragment key={page}>
                    {index > 0 && array[index - 1] !== page - 1 && (
                      <Text alignSelf="center">...</Text>
                    )}
                    <Button
                      onClick={() => handlePageChange(page)}
                      colorScheme={page === currentPage ? "teal" : "gray"}
                    >
                      {page}
                    </Button>
                  </React.Fragment>
                ))}
            </Stack>
            <Button
              onClick={() => handlePageChange(currentPage + 1)}
              isDisabled={currentPage === totalPages}
            >
              Next
            </Button>
          </Flex>
        </Box>
      )}
    </Box>
  );
};

const ExamResults = () => {
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState("");
  const [examResults, setExamResults] = useState([]);
  const [examCategories, setExamCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const selectedTabBg = useColorModeValue("teal.100", "teal.600");
  const selectedTabColor = "teal.800";
  const selectedTabFontWeight = "bold";
  const selectedTabBorderRadius = "md";
  const bgColor = useColorModeValue("white", "gray.800");

  const [academicId, setAcademicId] = useState(
    sessionStorage.getItem("Academic")
  );

  useEffect(() => {
    const handleStorageChange = () => {
      setAcademicId(sessionStorage.getItem("Academic"));
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const fetchExamData = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${BASE_URL}/admin/exam/list_result_to_classteacher.php`,
          {
            method: "POST",
            body: JSON.stringify({ pass: sessionStorage.getItem("user_id") }),
          }
        );
        const data = await response.json();
        console.log("teacher res", data);
        if (data.exam_result) {
          const filteredDetails = data.exam_result.filter(
            (detail) => detail.academic_year_id === academicId
          );
          setExamResults(filteredDetails);
          const categories = Array.from(
            new Set(filteredDetails.map((result) => result.category))
          );
          setExamCategories(categories);
        } else {
          setError(data.message || "Failed to fetch results");
        }
      } catch (err) {
        setError("Failed to fetch exam results");
      } finally {
        setLoading(false);
      }
    };

    fetchExamData();
  }, [academicId]);

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const groupResultsByCategory = (category) => {
    const results = examResults.filter(
      (result) => result.category === category
    );
    const students = Array.from(
      new Set(results.map((result) => result.studentname))
    );
    const subjects = Array.from(
      new Set(results.map((result) => result.subject))
    );

    const tableData = students.map((student) => {
      const studentData = { student };
      subjects.forEach((subject) => {
        const subjectData = results.find(
          (res) => res.studentname === student && res.subject === subject
        );
        studentData[subject] = subjectData || {};
      });
      return studentData;
    });

    return { students, subjects, tableData };
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="400px"
      >
        <Spinner size="xl" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  return (
    <Box bg={bgColor} borderRadius="lg" boxShadow="md" mx="auto">
      <div style={{ backgroundColor: "#FBFBFD" }}>
        <div className="teacher-list-container">
          <div className="header">
            <h1 style={{ fontSize: "20px", margin: 0 }}>Exam Result</h1>
            <div className="action-buttons">
              <Button
                className="refresh-button"
                boxShadow="md"
                backgroundColor="white"
                fontSize="11px"
                onClick={() => {
                  console.log("Refresh button clicked");
                }}
                display={{ base: "none", md: "flex" }}
              >
                <FaSync color="#515B73" />
              </Button>

              <div className="export-dropdown">
                <Button
                  className="export-button"
                  display={{ base: "none", md: "flex" }}
                  backgroundColor="white"
                  fontSize="11px"
                  boxShadow="md"
                >
                  <FaPrint color="#515B73" />
                  Print PDF
                </Button>
              </div>
            </div>
          </div>

          <div className="breadcrumb">
            <span>Staff</span> / <span>Exam</span> / <span>Exam Result </span>
          </div>
        </div>

        <Box p={6}>
          <Tabs
            isLazy
            variant="soft-rounded"
            colorScheme="teal"
            p={2}
            borderRadius="md"
          >
            <TabList mb={4}>
              {examCategories.map((category) => (
                <Tab
                  key={category}
                  _selected={{
                    bg: selectedTabBg,
                    color: selectedTabColor,
                    fontWeight: selectedTabFontWeight,
                    borderRadius: selectedTabBorderRadius,
                  }}
                  py={2}
                >
                  {category}
                </Tab>
              ))}
            </TabList>

            <Box className="sb" p={3}>
              <Flex
                direction={{ base: "column", md: "row" }}
                justify="space-between"
                align="center"
                gap={4}
              >
                {/* Rows Per Page Select */}
                <Select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  width={{ base: "100%", md: "120px" }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                </Select>

                {/* Search Input */}
                <InputGroup maxWidth={{ base: "100%", md: "300px" }}>
                  <InputLeftElement pointerEvents="none">
                    <FaSearch color="gray.300" />
                  </InputLeftElement>
                  <Input
                    placeholder="Search student name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </InputGroup>
              </Flex>
            </Box>

            <TabPanels>
              {examCategories.map((category) => (
                <TabPanel key={category} p={0} overflowX="auto">
                  <ResultsTable
                    categoryResults={groupResultsByCategory(category)}
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    handlePageChange={handlePageChange}
                    searchQuery={searchQuery}
                  />
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </Box>
      </div>
    </Box>
  );
};

export default ExamResults;
